import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  CloudOff,
  Download,
  ScanLine,
  Search,
  Users,
} from 'lucide-react';
import { arrivalState, type CheckInListRow, type CheckInResult } from '@peles/shared';
import { FilterSelect, VirtualList } from '../components/common';
import { SegmentedControl } from '../components/common/SegmentedControl';
import { useCheckIn, useCheckInList, type CheckInTarget } from '../hooks/useInvites';
import { useEvent } from '../hooks/useEvents';
import { downloadAttendanceCsv, type CheckInBody } from '../api/invites';
import { triggerDownload } from '../api/export';
import { CheckInToast, type CheckInToastState } from '../components/checkin/CheckInToast';
import { PartyArrivalModal, type PartyArrival } from '../components/checkin/PartyArrivalModal';
import { signalCheckIn, type CheckInTone } from '../components/checkin/feedback';
import {
  enqueueCheckIn,
  isOfflineError,
  readCheckInQueue,
  removeQueuedCheckIn,
  type QueuedCheckIn,
} from '../utils/checkInQueue';

/**
 * Extracts the RSVP token from a scanned QR value. Accepts a bare token, the
 * RSVP page URL, and the shareable preview URL (`/i/<token>`) — guests arrive
 * at the door with whichever link they were sent.
 */
export function tokenFromScan(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/\/(?:rsvp|i)\/([A-Za-z0-9-_]+)/);
  if (match) return match[1];
  return /^[A-Za-z0-9-_]{16,}$/.test(trimmed) ? trimmed : null;
}

type ArrivalFilter = 'all' | 'waiting' | 'partial' | 'arrived';

const FILTERS: { id: ArrivalFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'waiting', label: 'Not arrived' },
  { id: 'partial', label: 'Partly' },
  { id: 'arrived', label: 'Arrived' },
];

const NO_TABLE = 'none';
/** Results on screen at once; older ones go first. */
const MAX_TOASTS = 3;
const ROW_HEIGHT = 68;
/** The same code read again this soon is the camera seeing it twice, not a second guest. */
const RESCAN_GAP_MS = 4000;
/** How often waiting offline check-ins are tried again, besides when the browser comes online. */
const REPLAY_INTERVAL_MS = 20000;

const timeOf = (value: Date | string | null) =>
  value ? new Date(value).toLocaleTimeString('en-US', { timeStyle: 'short' }) : '';

function stateOf(row: CheckInListRow) {
  return arrivalState(row.arrivedCount, row.expected);
}

/** "Table 4 · 2 of 4 arrived" — what the door needs to send the party on. */
function describeArrival(guest: CheckInResult['guest']): string {
  const table = guest.tableNumber != null ? `Table ${guest.tableNumber}` : 'No table';
  const count =
    guest.expected > 0
      ? `${guest.arrivedCount} of ${guest.expected} arrived`
      : `${guest.arrivedCount} arrived`;
  return `${table} · ${count}`;
}

/**
 * Event-day check-in (redesign Phase 7): scan or tap and carry on — each
 * result is a banner with five seconds to undo, never a dialog; parties
 * arrive in part or whole, each named member ticked on their own; the list
 * filters by arrival and table, draws only what is on screen, and keeps
 * working without a connection, sending what it saved once one is back.
 */
export function CheckInPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: event } = useEvent(eventId ?? '');
  const { data: rows = [], isLoading } = useCheckInList(eventId);
  const checkIn = useCheckIn();
  // The mutation object is new on every render; the callbacks below read it
  // through a ref so they stay stable — the replay-on-open effect runs once.
  const mutate = useRef(checkIn.mutateAsync);
  mutate.current = checkIn.mutateAsync;

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ArrivalFilter>('all');
  const [tables, setTables] = useState<string[]>([]);
  const [editing, setEditing] = useState<CheckInListRow | null>(null);
  const [toasts, setToasts] = useState<CheckInToastState[]>([]);
  const [pending, setPending] = useState(() => (eventId ? readCheckInQueue(eventId).length : 0));
  // What was saved offline shows as done, from the queue itself — so it still
  // does after a reload with the connection still down.
  const [offlineRows, setOfflineRows] = useState(() =>
    offlineRowsOf(eventId ? readCheckInQueue(eventId) : []),
  );
  const [scanValue, setScanValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastScanRef = useRef<{ token: string; at: number } | null>(null);
  const toastId = useRef(0);
  const replaying = useRef(false);

  const show = useCallback((next: Omit<CheckInToastState, 'id'>) => {
    toastId.current += 1;
    signalCheckIn(next.tone);
    // Each result keeps its own five seconds and its own Undo: scanning the
    // next guest never takes the last one's away. Only the newest few stay.
    const id = toastId.current;
    setToasts((current) => [...current, { ...next, id }].slice(-MAX_TOASTS));
  }, []);
  const dismiss = useCallback(
    (id: number) => setToasts((current) => current.filter((t) => t.id !== id)),
    [],
  );

  // Navigating away mid-scan must release the camera: stop the tracks and
  // null the stream ref, which also ends the self-rescheduling detect loop.
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  /** A row as the door should show it: the server's, with anything saved offline over it. */
  const view = useMemo(
    () =>
      rows.map((row) => (offlineRows[row.guestId] ? { ...row, ...offlineRows[row.guestId] } : row)),
    [rows, offlineRows],
  );

  const totals = useMemo(() => {
    let expected = 0;
    let arrived = 0;
    let parties = 0;
    for (const row of view) {
      expected += row.expected;
      arrived += row.arrivedCount;
      if (row.arrivedCount > 0) parties += 1;
    }
    return { expected, arrived, parties };
  }, [view]);

  const tableOptions = useMemo(() => {
    const numbers = [...new Set(view.map((row) => row.tableNumber))].sort(
      (a, b) => (a ?? Infinity) - (b ?? Infinity),
    );
    return numbers.map((n) => ({
      id: n == null ? NO_TABLE : String(n),
      label: n == null ? 'No table' : `Table ${n}`,
    }));
  }, [view]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return view.filter((row) => {
      const state = stateOf(row);
      if (filter === 'waiting' && state !== 'NOT_ARRIVED') return false;
      if (filter === 'partial' && state !== 'PARTIAL') return false;
      if (filter === 'arrived' && state !== 'ARRIVED') return false;
      if (
        tables.length &&
        !tables.includes(row.tableNumber == null ? NO_TABLE : String(row.tableNumber))
      )
        return false;
      if (!query) return true;
      return [row.guestName, row.familyGroup, ...row.members.map((m) => m.name)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query);
    });
  }, [view, search, filter, tables]);

  /** Put a party back where it stood before a check-in — the toast's Undo. */
  const undoTo = useCallback(
    (result: CheckInResult) => {
      const { guest, previous } = result;
      mutate
        .current({
          target: { guestId: guest.guestId },
          // Only if nobody has touched the party since: an undo never takes
          // back an arrival another door recorded in the meantime.
          body: {
            eventId,
            arrived: previous.arrivedCount,
            memberIds: previous.memberIds,
            ifState: doorState(guest),
          },
        })
        .then((undone) => show({ tone: 'info', title: `${undone.guest.guestName}: undone` }))
        .catch((err: unknown) =>
          show(
            isChangedElsewhere(err)
              ? {
                  tone: 'warning',
                  title: `${guest.guestName} was changed at another door`,
                  detail: 'Nothing was undone — open the party to check it.',
                }
              : {
                  tone: 'error',
                  title: 'Could not undo',
                  detail: err instanceof Error ? err.message : undefined,
                },
          ),
        );
    },
    [eventId, show],
  );

  /** What the door is told of a check-in that went through. */
  const announce = useCallback(
    (result: CheckInResult, body: CheckInBody, scanned: boolean) => {
      const { guest, previous, undone } = result;
      const onUndo = () => undoTo(result);
      if (undone) {
        show({ tone: 'info', title: `${guest.guestName}: not arrived`, onUndo });
        return;
      }
      let tone: CheckInTone = 'success';
      let title = `Welcome, ${guest.guestName}`;
      if (scanned && previous.arrivedCount > 0) {
        tone = 'warning';
        title = `${guest.guestName} — already checked in at ${timeOf(previous.checkedInAt)}`;
      } else if (guest.expected === 0) {
        tone = 'warning';
        title = `${guest.guestName} said they were not coming`;
      } else if (stateOfSummary(guest) === 'PARTIAL') {
        tone = 'warning';
      }
      show({ tone, title, detail: describeArrival(guest), onUndo: body.undo ? undefined : onUndo });
    },
    [show, undoTo],
  );

  /**
   * Send a check-in; if there is no connection, keep it for later and show
   * the party as arrived meanwhile. A refusal is shown and never kept.
   */
  const submit = useCallback(
    async (
      target: CheckInTarget,
      body: CheckInBody,
      label: string,
      optimistic?: Partial<CheckInListRow>,
    ) => {
      if (!eventId) return;
      const scanned = 'token' in target;
      try {
        const result = await mutate.current({ target, body: { ...body, eventId } });
        if ('guestId' in target) setOfflineRows(({ [target.guestId]: _dropped, ...rest }) => rest);
        announce(result, body, scanned);
      } catch (err) {
        if (isOfflineError(err)) {
          const kept = enqueueCheckIn(eventId, {
            target,
            body: { ...body, eventId },
            label,
            ...(optimistic ? { optimistic } : {}),
          });
          if (!kept) {
            show({
              tone: 'error',
              title: 'No connection',
              detail: 'This check-in could not be saved.',
            });
            return;
          }
          setPending(readCheckInQueue(eventId).length);
          if (optimistic && 'guestId' in target)
            setOfflineRows((current) => ({ ...current, [target.guestId]: optimistic }));
          show({
            tone: 'info',
            title: `${label} — saved offline`,
            detail: 'It will be sent when the connection is back.',
          });
          return;
        }
        show(
          isChangedElsewhere(err)
            ? {
                tone: 'warning',
                title: `${label} was changed at another door`,
                detail: 'Nothing was saved — open the party to check it.',
              }
            : {
                tone: 'error',
                title: scanned ? 'Code not accepted' : `Could not check in ${label}`,
                detail: err instanceof Error ? err.message : undefined,
              },
        );
      }
    },
    [announce, eventId, show],
  );

  /** Send what was saved offline, oldest first, each with the time it was scanned. */
  const replay = useCallback(async () => {
    if (!eventId || replaying.current) return;
    const queue = readCheckInQueue(eventId);
    if (!queue.length) return;
    replaying.current = true;
    try {
      for (const item of queue) {
        try {
          await mutate.current({
            target: item.target,
            body: { ...item.body, at: item.queuedAt },
          });
          removeQueuedCheckIn(eventId, item.id);
        } catch (err) {
          if (isOfflineError(err)) break;
          removeQueuedCheckIn(eventId, item.id);
          show(
            isChangedElsewhere(err)
              ? {
                  tone: 'warning',
                  title: `${item.label} was changed at another door`,
                  detail: 'The change saved offline was not applied — check the party again.',
                }
              : {
                  tone: 'error',
                  title: `Could not check in ${item.label}`,
                  detail: err instanceof Error ? err.message : undefined,
                },
          );
        }
      }
    } finally {
      replaying.current = false;
      const left = readCheckInQueue(eventId);
      setPending(left.length);
      setOfflineRows(offlineRowsOf(left));
    }
  }, [eventId, show]);

  useEffect(() => {
    if (!pending) return;
    const onOnline = () => void replay();
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(() => void replay(), REPLAY_INTERVAL_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(timer);
    };
  }, [pending, replay]);

  // Anything left from a session that lost its connection goes as soon as the page opens.
  useEffect(() => {
    void replay();
  }, [replay]);

  /** The quick button: the whole party is here (the rest of it, if some came already). */
  const checkInWholeParty = (row: CheckInListRow) => {
    // As the server will: everybody coming, nobody already here taken out,
    // and a count never below the names — so a later offline edit of the
    // party starts from exactly what the server will hold.
    const here = row.members.filter((m) => m.attending !== false || m.checkedInAt).map((m) => m.id);
    return submit({ guestId: row.guestId }, {}, row.guestName, {
      arrivedCount: Math.max(row.expected, 1, row.arrivedCount, here.length),
      checkedInAt: row.checkedInAt ?? new Date(),
      members: withArrived(row, here),
    });
  };

  const saveArrival = async (row: CheckInListRow, arrival: PartyArrival) => {
    setEditing(null);
    await submit(
      { guestId: row.guestId },
      // Made against the party as the door saw it — if another door has
      // changed it since, this is refused rather than overwriting theirs,
      // now or when replayed from offline.
      { arrived: arrival.arrived, memberIds: arrival.memberIds, ifState: doorState(row) },
      row.guestName,
      {
        arrivedCount: arrival.arrived,
        checkedInAt: arrival.arrived > 0 ? (row.checkedInAt ?? new Date()) : null,
        // The names too, so a second edit made offline starts from this one.
        members: withArrived(row, arrival.memberIds),
      },
    );
  };

  const handleTokenSubmit = async (raw: string) => {
    const token = tokenFromScan(raw);
    if (!token) {
      show({ tone: 'warning', title: 'Not a valid RSVP link or code' });
      return;
    }
    setScanValue('');
    await submit({ token }, {}, 'Scanned code');
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const startCamera = async () => {
    const DetectorCtor = (
      window as unknown as {
        BarcodeDetector?: new (options: { formats: string[] }) => {
          detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]>;
        };
      }
    ).BarcodeDetector;
    if (!DetectorCtor) {
      show({
        tone: 'warning',
        title: 'Camera scanning is not supported here',
        detail: 'Paste the link instead.',
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setScanning(true);
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
      const detector = new DetectorCtor({ formats: ['qr_code'] });
      // The camera stays on between guests: each code is checked in and the
      // scanning goes on, skipping the same code while it is still in view.
      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await detector.detect(video);
          const token = codes.map((c) => tokenFromScan(c.rawValue)).find(Boolean);
          const last = lastScanRef.current;
          if (token && !(last && last.token === token && Date.now() - last.at < RESCAN_GAP_MS)) {
            lastScanRef.current = { token, at: Date.now() };
            await submit({ token }, {}, 'Scanned code');
          }
        } catch {
          // detection errors are transient — keep scanning
        }
        requestAnimationFrame(() => void tick());
      };
      requestAnimationFrame(() => void tick());
    } catch {
      stopCamera();
      show({ tone: 'error', title: 'Could not access the camera' });
    }
  };

  const exportCsv = async () => {
    if (!eventId) return;
    setExporting(true);
    try {
      triggerDownload(await downloadAttendanceCsv(eventId), 'attendance.csv');
    } catch (err) {
      show({
        tone: 'error',
        title: 'Could not export attendance',
        detail: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setExporting(false);
    }
  };

  const share = totals.expected > 0 ? Math.min(100, (totals.arrived / totals.expected) * 100) : 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4 pb-28" data-testid="check-in-page">
      <div className="flex items-center gap-3">
        <Link
          to={`/events/${eventId}`}
          aria-label="Back to event"
          className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold text-gray-900 truncate">Guest Check-In</h1>
          {event?.name && <p className="text-sm text-gray-500 truncate">{event.name}</p>}
        </div>
        <button
          type="button"
          onClick={() => void exportCsv()}
          disabled={exporting}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>

      {pending > 0 && (
        <div
          className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
          role="status"
        >
          <CloudOff className="w-4 h-4 shrink-0" />
          <span className="flex-1">
            {pending} check-in{pending === 1 ? '' : 's'} waiting to be sent
          </span>
          <button
            type="button"
            onClick={() => void replay()}
            className="font-medium underline underline-offset-2"
          >
            Send now
          </button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div aria-live="polite">
            <span className="text-3xl font-bold text-gray-900" data-testid="people-arrived">
              {totals.arrived} / {totals.expected}
            </span>{' '}
            <span className="text-sm text-gray-500">arrived</span>
          </div>
          <div className="text-sm text-gray-500 inline-flex items-center gap-1">
            <Users className="w-4 h-4" />
            {totals.parties} of {view.length} parties
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-green-500 transition-all" style={{ width: `${share}%` }} />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
            <ScanLine className="w-4 h-4 text-gray-400" />
            Scan QR
          </h2>
          <button
            type="button"
            onClick={() => (scanning ? stopCamera() : void startCamera())}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Camera className="w-4 h-4" />
            {scanning ? 'Stop camera' : 'Use camera'}
          </button>
        </div>
        <video
          ref={videoRef}
          className={scanning ? 'w-full rounded-lg' : 'hidden'}
          muted
          playsInline
        />
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleTokenSubmit(scanValue);
          }}
        >
          <input
            type="text"
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            placeholder="Paste RSVP link or code"
            aria-label="RSVP link or code"
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            Check in
          </button>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guests…"
            aria-label="Search guests"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            variant="chips"
            ariaLabel="Arrival"
            options={FILTERS}
            value={filter}
            onChange={setFilter}
          />
          <FilterSelect
            label="Table"
            items={tableOptions}
            value={tables}
            onChange={(next) => setTables(next as string[])}
            getValue={(o) => o.id}
            getLabel={(o) => o.label}
            multiple
            searchable
          />
        </div>
        {isLoading ? (
          <div className="space-y-2 animate-pulse" role="status" aria-label="Loading guests">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 rounded-lg bg-gray-100" />
            ))}
          </div>
        ) : (
          <VirtualList
            items={filtered}
            rowHeight={ROW_HEIGHT}
            height={560}
            ariaLabel="Guests"
            getKey={(row) => row.guestId}
            empty={<p className="text-sm text-gray-500 py-4 text-center">No guests found.</p>}
            renderRow={(row) => (
              <CheckInRow
                row={row}
                onOpen={() => setEditing(row)}
                onCheckIn={() => void checkInWholeParty(row)}
              />
            )}
          />
        )}
      </div>

      <PartyArrivalModal
        row={editing}
        onClose={() => setEditing(null)}
        onSave={(arrival) => editing && void saveArrival(editing, arrival)}
        saving={checkIn.isPending}
      />
      <CheckInToast toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

/** The party as the door saw it: how many are here and which named people. */
function doorState(guest: Pick<CheckInListRow, 'arrivedCount' | 'members'>) {
  return {
    arrivedCount: guest.arrivedCount,
    memberIds: guest.members.filter((m) => m.checkedInAt).map((m) => m.id),
  };
}

/** The party's named members with exactly `hereIds` arrived, keeping any earlier time. */
function withArrived(row: CheckInListRow, hereIds: string[]): CheckInListRow['members'] {
  const here = new Set(hereIds);
  const now = new Date();
  return row.members.map((m) => ({
    ...m,
    checkedInAt: here.has(m.id) ? (m.checkedInAt ?? now) : null,
  }));
}

/** The server refused because the party changed since the door looked (409). */
function isChangedElsewhere(err: unknown): boolean {
  return (
    !!err &&
    typeof err === 'object' &&
    (err as { response?: { status?: number } }).response?.status === 409
  );
}

/** Rows saved offline, by guest, as the door should show them meanwhile. */
function offlineRowsOf(queue: QueuedCheckIn[]): Record<string, Partial<CheckInListRow>> {
  const rows: Record<string, Partial<CheckInListRow>> = {};
  for (const item of queue) {
    if ('guestId' in item.target && item.optimistic) {
      rows[item.target.guestId] = { ...rows[item.target.guestId], ...item.optimistic };
    }
  }
  return rows;
}

function stateOfSummary(guest: CheckInResult['guest']) {
  return arrivalState(guest.arrivedCount, guest.expected);
}

/** One party: who, where, how many are here — tap the name to count them in by hand. */
function CheckInRow({
  row,
  onOpen,
  onCheckIn,
}: {
  row: CheckInListRow;
  onOpen: () => void;
  onCheckIn: () => void;
}) {
  const state = stateOf(row);
  const firstName = row.guestName.split(' ')[0];
  return (
    <div className="flex h-full items-center justify-between gap-3 border-b border-gray-100">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Who of ${row.guestName}'s party is here`}
        className="min-w-0 flex-1 text-left py-2"
      >
        <div className="font-medium text-gray-900 truncate">{row.guestName}</div>
        <div className="text-xs text-gray-500 truncate">
          {row.tableNumber != null ? `Table ${row.tableNumber}` : 'No table'}
          {row.expected === 0 ? ' · said not coming' : ` · party of ${row.expected}`}
          {row.checkedInAt && ` · in at ${timeOf(row.checkedInAt)}`}
        </div>
      </button>
      {state === 'NOT_ARRIVED' ? (
        <button
          type="button"
          aria-label={`Check in ${firstName}`}
          onClick={onCheckIn}
          className="min-h-11 px-4 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          Check in
        </button>
      ) : state === 'PARTIAL' ? (
        <button
          type="button"
          aria-label={`The rest of ${firstName}'s party arrived`}
          onClick={onCheckIn}
          className="min-h-11 px-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
        >
          {row.arrivedCount} of {row.expected}
        </button>
      ) : (
        <button
          type="button"
          aria-label={`${firstName} arrived — change`}
          onClick={onOpen}
          className="inline-flex items-center gap-1.5 min-h-11 px-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
        >
          <CheckCircle2 className="w-4 h-4" />
          {row.arrivedCount}
        </button>
      )}
    </div>
  );
}

export default CheckInPage;
